import { z } from "zod";

import { isValidHexAttestation } from "../domains";
import { CCTPProxyError } from "../errors";
import { SUI_CCTP_DOMAIN_ID } from "../nonce/contracts";
import type { CCTPVersion } from "../version";
import { appCctpNetwork } from "./contracts";
import { MIN_FINALITY_THRESHOLD } from "./fees";

export type IrisAttestationStatus = "complete" | "pending_confirmations";

export type IrisDelayReason =
  | "insufficient_fee"
  | "amount_above_max"
  | "insufficient_allowance_available";

/** One message from Circle's v2 messages endpoint, which serves v1 and v2 transfers alike. */
export interface IrisMessage {
  cctpVersion: CCTPVersion;
  status: IrisAttestationStatus;
  /** Null until attested: Circle returns "0x" before then. */
  message: string | null;
  /** Null until attested: Circle returns "PENDING" before then. */
  attestation: string | null;
  eventNonce: string | null;
  /** Why a Fast Transfer is waiting for Standard finality, when Circle says. */
  delayReason: IrisDelayReason | null;
}

export interface IrisRouteFees {
  /** Null when Circle doesn't offer Fast Transfer on the route. */
  fastBps: number | null;
  standardBps: number;
}

const DELAY_REASONS: readonly string[] = [
  "insufficient_fee",
  "amount_above_max",
  "insufficient_allowance_available",
] satisfies IrisDelayReason[];

const IrisMessageSchema = z.object({
  cctpVersion: z.union([z.literal(1), z.literal(2)]),
  status: z.enum(["complete", "pending_confirmations"]),
  message: z.string().nullish(),
  attestation: z.string().nullish(),
  eventNonce: z.string().nullish(),
  delayReason: z.string().nullish(),
});

const IrisMessagesResponseSchema = z.object({ messages: z.array(IrisMessageSchema) });

const IrisFeesResponseSchema = z.array(
  z.object({ finalityThreshold: z.number(), minimumFee: z.number().nonnegative() }),
);

const IrisAllowanceResponseSchema = z.object({ allowance: z.number().nonnegative() });

const MESSAGES_PATH = "/v2/messages";
const FEES_PATH = "/v2/burn/USDC/fees";
const SANDBOX_UNKNOWN_ROUTE = "Invalid source/destination domain id";
const FAST_ALLOWANCE_PATH = "/v2/fastBurn/USDC/allowance";

// Same-origin proxy: sidesteps geo-blocks, CORS and content blockers that hit Circle directly.
async function irisFetch(path: string): Promise<Response> {
  const response = await fetch(`/api/cctp${path}`);
  if (!response.ok && response.status !== 404) throw new CCTPProxyError(response.status);
  return response;
}

/**
 * Messages Circle has observed for a source transaction.
 * Returns null when Circle hasn't seen the transaction yet; throws `CCTPProxyError` on an outage.
 */
export async function fetchIrisMessages({
  domainId,
  txHash,
}: {
  domainId: number;
  txHash: string;
}): Promise<IrisMessage[] | null> {
  const response = await irisFetch(`${MESSAGES_PATH}/${domainId}?transactionHash=${txHash}`);
  if (response.status === 404) return null;

  const parsed = IrisMessagesResponseSchema.safeParse(await response.json());
  if (!parsed.success) return null;

  return parsed.data.messages.map((m) => ({
    cctpVersion: m.cctpVersion,
    status: m.status,
    message: m.message && m.message !== "0x" ? m.message : null,
    attestation: isValidHexAttestation(m.attestation) ? m.attestation : null,
    eventNonce: m.eventNonce ?? null,
    delayReason:
      m.delayReason && DELAY_REASONS.includes(m.delayReason)
        ? (m.delayReason as IrisDelayReason)
        : null,
  }));
}

/** Circle's current fee rates for a route, in basis points. */
export async function fetchIrisRouteFees({
  sourceDomainId,
  destDomainId,
}: {
  sourceDomainId: number;
  destDomainId: number;
}): Promise<IrisRouteFees> {
  const response = await fetch(`/api/cctp${FEES_PATH}/${sourceDomainId}/${destDomainId}`);
  if (
    response.status === 400 &&
    appCctpNetwork() === "testnet" &&
    (sourceDomainId === SUI_CCTP_DOMAIN_ID || destDomainId === SUI_CCTP_DOMAIN_ID) &&
    (await response.clone().text()).includes(SANDBOX_UNKNOWN_ROUTE)
  ) {
    // Circle's sandbox doesn't quote Sui yet; mainnet quotes every Sui route at 0 bps.
    return { fastBps: null, standardBps: 0 };
  }
  if (!response.ok) throw new CCTPProxyError(response.status);
  const tiers = IrisFeesResponseSchema.parse(await response.json());

  // The same thresholds the burns encode, so the quoted tier is the one Circle attests at.
  const standard = tiers.find((t) => t.finalityThreshold >= MIN_FINALITY_THRESHOLD.standard);
  if (!standard) {
    throw new Error(`Circle returned no Standard fee for route ${sourceDomainId}→${destDomainId}`);
  }
  const fast = tiers.find((t) => t.finalityThreshold <= MIN_FINALITY_THRESHOLD.fast);

  return { fastBps: fast?.minimumFee ?? null, standardBps: standard.minimumFee };
}

export type IrisReattestOutcome =
  /** Circle is re-attesting; poll messages for the renewed attestation. */
  | "started"
  /** Circle already holds a finalized attestation, which never expires; poll messages for it. */
  | "finalized"
  /** Circle doesn't know the nonce, or hasn't attested it yet. */
  | "not_found";

const NONCE_PATTERN = /^0x[0-9a-fA-F]{64}$/;
// Circle's sandbox accepts a reattest POST and never answers it.
const REATTEST_TIMEOUT_MS = 20_000;

const IrisReattestErrorSchema = z.object({ error: z.string().optional() });

/** Asks Circle to re-attest an expired Fast Transfer by its v2 nonce. Throws on an outage. */
export async function requestIrisReattest(
  nonce: string,
  { timeoutMs = REATTEST_TIMEOUT_MS }: { timeoutMs?: number } = {},
): Promise<IrisReattestOutcome> {
  if (!NONCE_PATTERN.test(nonce)) throw new Error(`Not a CCTP v2 nonce: ${nonce}`);
  const response = await fetch(`/api/cctp/v2/reattest/${nonce}`, {
    method: "POST",
    signal: AbortSignal.timeout(timeoutMs),
  });
  if (response.ok) return "started";
  if (response.status === 404) return "not_found";
  if (response.status === 400) {
    const { error } = IrisReattestErrorSchema.parse(await response.json().catch(() => ({})));
    if (error?.includes("already finalized")) return "finalized";
  }
  throw new CCTPProxyError(response.status);
}

/** Circle's remaining global Fast Transfer allowance, in whole USDC. */
export async function fetchIrisFastAllowance(): Promise<number> {
  const response = await irisFetch(FAST_ALLOWANCE_PATH);
  return IrisAllowanceResponseSchema.parse(await response.json()).allowance;
}
